/**
 * CC-60 → CC-63 — DemiQuotes
 *
 * Renders a Weekly Quotes brief pushed by Claude Pro via the Hail Mary MCP
 * connector. Shape: weekly_quotes.quotes = {
 *   imported_source, imported_at, product,
 *   imported_raw: { cards: [{
 *     quote, author_name, author_title, author_company, source_url,
 *     image_url?, tone?: "dark" | "light"
 *   }] }
 * }
 *
 * CC-63 — Dark/Light pair layout:
 * Claude typically pushes TWO variants per quote (one dark + one light,
 * baked PNGs). We group cards by (quote + author_name) and render each
 * pair as a 2-col row (dark left, light right on md+). Singletons span
 * full width. Legacy data without `tone` falls back to the previous
 * single-column stacked layout.
 *
 * All text fields are plain text — React escapes them (no
 * `dangerouslySetInnerHTML`).
 */
import { useState } from "react";
import { Sparkles, ExternalLink, Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function resolveImageUrl(raw) {
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) return `${API_BASE}${raw}`;
  return raw;
}

function hostOf(url) {
  if (!url) return "";
  try { return new URL(url).host.replace(/^www\./, ""); }
  catch { return ""; }
}

function pairKey(card) {
  const q = (card.quote || "").trim().toLowerCase();
  const a = (card.author_name || "").trim().toLowerCase();
  return `${q}|${a}`;
}

function normalizedTone(card) {
  const t = (card.tone || "").toString().trim().toLowerCase();
  if (t === "dark" || t === "light") return t;
  return null;
}

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function McpBadge({ className = "" }) {
  return (
    <span
      data-testid="demi-quotes-mcp-badge"
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 mono uppercase tracking-widest text-[9px] ${className}`}
      style={{
        background: "var(--hm-accent-soft)",
        borderColor: "var(--hm-accent-dim)",
        color: "var(--hm-accent)",
      }}
      title="Pushed by Claude Pro via the Hail Mary MCP connector"
    >
      <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
      MCP
    </span>
  );
}

async function downloadImage(url, filename) {
  try {
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    toast.success("Image downloaded", { description: filename });
  } catch (e) {
    toast.error("Download failed", { description: String(e.message || e) });
  }
}

async function copyAbsoluteUrl(url) {
  const abs = url.startsWith("http") ? url : `${window.location.origin}${url}`;
  try {
    await navigator.clipboard.writeText(abs);
    toast.success("URL copied to clipboard");
  } catch (e) {
    toast.error("Copy failed", { description: String(e.message || e) });
  }
}

/**
 * Single card. Props:
 *  - card: raw card from payload
 *  - index: 0-based within pair (used in data-testid)
 *  - tone: "dark" | "light" | null  (null = legacy fallback)
 *  - testIdSuffix: unique suffix for data-testids within the pair
 */
function Card({ card, index, tone, testIdSuffix }) {
  const tc = [card.author_title, card.author_company].filter(Boolean).join(" · ");
  const host = hostOf(card.source_url);
  const imageSrc = resolveImageUrl(card.image_url);
  const [imgFailed, setImgFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const testIdBase = tone
    ? `demi-quote-card-${tone}-${testIdSuffix}`
    : `demi-quote-card-${index + 1}`;

  const toneLabel = tone ? tone : null;
  const authorSlug = slugify(card.author_name) || "quote";
  const filename = tone
    ? `demi-${authorSlug}-${tone}.png`
    : `demi-${authorSlug}.png`;

  const onCopy = async () => {
    await copyAbsoluteUrl(imageSrc);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // --- Image render path (full-bleed, Claude bakes the design) ---
  if (imageSrc && !imgFailed) {
    return (
      <div
        className="hm-surface relative overflow-hidden group rounded-lg"
        data-testid={testIdBase}
      >
        {toneLabel && (
          <div className="absolute top-3 right-3 z-10">
            <Badge
              variant="secondary"
              aria-label={`tone: ${toneLabel}`}
              className="font-mono text-[10px] uppercase tracking-wider opacity-70 group-hover:opacity-100 transition pointer-events-none"
            >
              {toneLabel}
            </Badge>
          </div>
        )}

        <img
          src={imageSrc}
          alt={(card.quote || "Quote of the week").slice(0, 120)}
          className="block w-full h-auto"
          onError={() => setImgFailed(true)}
          data-testid={`${testIdBase}-image`}
          loading="lazy"
        />

        {/* Hover action bar — bottom overlay */}
        <div
          className="absolute bottom-0 inset-x-0 p-2 flex gap-1.5 justify-end
                     bg-gradient-to-t from-black/70 via-black/30 to-transparent
                     opacity-0 group-hover:opacity-100 transition
                     pointer-events-none group-hover:pointer-events-auto"
        >
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => downloadImage(imageSrc, filename)}
            title="Download PNG"
            className="h-8 px-2 text-white hover:bg-white/15"
            data-testid={`${testIdBase}-download`}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCopy}
            title="Copy image URL"
            className="h-8 px-2 text-white hover:bg-white/15"
            data-testid={`${testIdBase}-copy`}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>
    );
  }

  // --- Text fallback (image missing or failed to load) ---
  const toneFallbackClass =
    tone === "dark"
      ? "bg-black text-white"
      : tone === "light"
      ? "bg-white text-black border border-black/10"
      : "hm-surface";

  return (
    <div
      className={`${toneFallbackClass} relative p-5 rounded-lg overflow-hidden`}
      data-testid={testIdBase}
    >
      {toneLabel && (
        <div className="absolute top-3 right-3 z-10">
          <Badge
            variant="secondary"
            aria-label={`tone: ${toneLabel}`}
            className="font-mono text-[10px] uppercase tracking-wider opacity-80 pointer-events-none"
          >
            {toneLabel}
          </Badge>
        </div>
      )}
      <div
        className="font-serif leading-relaxed pr-16"
        style={{ fontSize: 16, lineHeight: 1.55 }}
        data-testid={`${testIdBase}-text`}
      >
        &ldquo;{card.quote}&rdquo;
      </div>
      <div className={`mt-4 pt-3 border-t ${tone === "dark" ? "border-white/20" : tone === "light" ? "border-black/10" : "border-hm-border"}`}>
        <div className="text-sm font-semibold" data-testid={`${testIdBase}-author`}>
          {card.author_name || <span className="italic opacity-60">Unknown author</span>}
        </div>
        {tc && (
          <div className={`mono text-[11px] mt-1 ${tone === "dark" ? "text-white/70" : tone === "light" ? "text-black/60" : "text-hm-fg-muted"}`}>
            {tc}
          </div>
        )}
        {card.source_url && (
          <a
            href={card.source_url}
            target="_blank"
            rel="noreferrer noopener"
            className="mono text-[11px] mt-2 inline-flex items-center gap-1 hover:underline"
            style={{ color: tone === "dark" ? "#7FFFC4" : "var(--hm-accent)" }}
            data-testid={`${testIdBase}-source`}
          >
            {host || "source"}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Group cards into pairs keyed by (quote + author).
 * Returns an array of { key, dark, light, others } groups preserving the
 * first-seen order from the raw array.
 */
function groupPairs(cards) {
  const order = [];
  const map = new Map();
  cards.forEach((c, i) => {
    const k = pairKey(c);
    if (!map.has(k)) {
      map.set(k, { key: k, firstIndex: i, dark: null, light: null, others: [] });
      order.push(k);
    }
    const g = map.get(k);
    const t = normalizedTone(c);
    if (t === "dark" && !g.dark) g.dark = c;
    else if (t === "light" && !g.light) g.light = c;
    else g.others.push(c);
  });
  return order.map((k) => map.get(k));
}

export default function DemiQuotes({ payload }) {
  const raw = (payload && payload.imported_raw) || {};
  const cards = Array.isArray(raw.cards) ? raw.cards : [];
  const importedAt = payload?.imported_at;

  if (!cards.length) {
    return (
      <div className="hm-surface p-10 text-center" data-testid="demi-quotes-empty">
        <Sparkles className="h-8 w-8 text-hm-fg-subtle mx-auto mb-3" />
        <div className="text-hm-fg font-medium">No quote cards in this brief.</div>
        <p className="text-sm text-hm-fg-muted mt-2 max-w-md mx-auto">
          Claude pushed an empty payload. Re-run{" "}
          <code className="mono text-xs">push_brief product=&quot;quotes&quot;</code> from
          Claude.ai.
        </p>
      </div>
    );
  }

  const hasAnyTone = cards.some((c) => normalizedTone(c) !== null);

  const header = (
    <div
      className="flex items-center gap-3 mb-4 px-4 py-3 rounded-lg"
      style={{
        background: "var(--hm-accent-soft)",
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: "var(--hm-accent-dim)",
      }}
      data-testid="demi-quotes-source-banner"
    >
      <Sparkles className="h-4 w-4 shrink-0" style={{ color: "var(--hm-accent)" }} />
      <div className="min-w-0 flex-1">
        <div
          className="mono uppercase tracking-widest"
          style={{ fontSize: 10, color: "var(--hm-accent-dim)" }}
        >
          Demi · pushed via MCP
        </div>
        <div className="text-hm-fg text-sm mt-0.5">
          {cards.length} quote card{cards.length === 1 ? "" : "s"} composed by
          Claude Pro and pushed into Hail Mary through{" "}
          <code className="mono text-xs">push_brief product=&quot;quotes&quot;</code>.
        </div>
        {importedAt && (
          <div className="mono text-[10px] text-hm-fg-subtle mt-1">
            imported_at · {importedAt}
          </div>
        )}
      </div>
    </div>
  );

  // --- Legacy fallback: no `tone` on any card → single-column stacked view ---
  if (!hasAnyTone) {
    return (
      <div data-testid="demi-quotes-body">
        {header}
        <div className="grid grid-cols-1 gap-4">
          {cards.map((c, i) => (
            <Card key={i} card={c} index={i} tone={null} testIdSuffix={i + 1} />
          ))}
        </div>
      </div>
    );
  }

  // --- Pair layout (dark left / light right on md+) ---
  const pairs = groupPairs(cards);

  return (
    <div data-testid="demi-quotes-body">
      {header}
      <div className="space-y-6" data-testid="demi-quotes-pairs">
        {pairs.map((p, pIdx) => {
          const slots = [];
          if (p.dark) slots.push({ card: p.dark, tone: "dark" });
          if (p.light) slots.push({ card: p.light, tone: "light" });
          p.others.forEach((c) => slots.push({ card: c, tone: null }));

          const pairSuffix = pIdx + 1;
          const singleton = slots.length === 1;

          return (
            <div
              key={p.key}
              className={singleton
                ? "grid grid-cols-1"
                : "grid grid-cols-1 md:grid-cols-2 gap-4"}
              data-testid={`demi-quote-pair-${pairSuffix}`}
            >
              {slots.map((s, i) => (
                <Card
                  key={`${p.key}-${s.tone || "x"}-${i}`}
                  card={s.card}
                  index={i}
                  tone={s.tone}
                  testIdSuffix={pairSuffix}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
